import React from 'react';
import { Shield, Crown, Star } from 'lucide-react';

/**
 * Leadership badge tiers:
 *   Level 1 — Founder / President / Vice-President: Gold/Red "Official"
 *   Level 2 — Executive Board / Board of Directors: Navy premium
 *   Level 3 — Committee Directors: Subtle professional
 */

const BADGE_CONFIG = {
  chamber_founder: { label: 'Founder', level: 1 },
  chamber_president: { label: 'President', level: 1 },
  chamber_vp: { label: 'Vice President', level: 1 },
  chamber_executive: { label: 'Executive Board', level: 2 },
  chamber_director: { label: 'Board of Directors', level: 2 },
  committee_director: { label: 'Committee Director', level: 3 },
};

// Returns the highest leadership badge from badges array
export const getLeadershipInfo = (badges = [], leadershipLevel, leadershipTitle) => {
  if (!leadershipLevel && (!badges || badges.length === 0)) return null;
  
  // Find the leadership badge
  const leadershipBadge = badges.find(b => BADGE_CONFIG[b]);
  const config = leadershipBadge ? BADGE_CONFIG[leadershipBadge] : null;
  const level = leadershipLevel || config?.level;
  
  if (!level) return null;
  
  return {
    level,
    badge: leadershipBadge,
    label: config?.label || leadershipTitle || 'Leadership',
    title: leadershipTitle || config?.label,
  };
};

// Inline badge — used next to names in posts, comments, cards
export const LeadershipBadge = ({ badges = [], leadershipLevel, leadershipTitle, size = 'sm' }) => {
  const info = getLeadershipInfo(badges, leadershipLevel, leadershipTitle);
  if (!info) return null;

  if (info.level === 1) {
    // Tier 1 — Gold/Red "Official" — most prestigious
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold rounded ${
          size === 'lg'
            ? 'text-xs px-2.5 py-1'
            : size === 'md'
            ? 'text-[11px] px-2 py-0.5'
            : 'text-[10px] px-1.5 py-0.5'
        }`}
        style={{
          background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 40%, #CD1125 100%)',
          color: '#fff',
          boxShadow: '0 1px 3px rgba(205,17,37,0.3)',
        }}
        data-testid="leadership-badge-tier1"
      >
        <Crown className={size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
        Official
      </span>
    );
  }

  if (info.level === 2) {
    // Tier 2 — Navy premium badge
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold rounded bg-[#002249] text-white ${
          size === 'lg'
            ? 'text-xs px-2.5 py-1'
            : size === 'md'
            ? 'text-[11px] px-2 py-0.5'
            : 'text-[10px] px-1.5 py-0.5'
        }`}
        data-testid="leadership-badge-tier2"
      >
        <Shield className={size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
        Board
      </span>
    );
  }

  // Tier 3 — Committee, subtle
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold rounded bg-slate-600 text-white ${
        size === 'lg'
          ? 'text-xs px-2.5 py-1'
          : size === 'md'
          ? 'text-[11px] px-2 py-0.5'
          : 'text-[10px] px-1.5 py-0.5'
      }`}
      data-testid="leadership-badge-tier3"
    >
      <Star className={size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
      Committee
    </span>
  );
};

// Title label — shows the specific role title under the badge
export const LeadershipTitle = ({ badges = [], leadershipLevel, leadershipTitle, className = '' }) => {
  const info = getLeadershipInfo(badges, leadershipLevel, leadershipTitle);
  if (!info) return null;
  
  const color = info.level === 1 ? 'text-[#B8860B]' : info.level === 2 ? 'text-[#002249]' : 'text-slate-500';
  
  return (
    <span className={`text-[11px] font-semibold ${color} ${className}`} data-testid="leadership-title-label">
      {info.title}
    </span>
  );
};

export default LeadershipBadge;
